import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import api from '../services/api';
import Toast from '../components/Toast';

const CDN_BASE_URL = 'https://cdn.jsdelivr.net/gh/JahelCuadrado/ExerciseGymGifsDB@v1.1.0';

const CDN_MUSCLES_ALL = [
  { id: 'abductors',         category: 'legs',      muscle_group: 'Abductores' },
  { id: 'abs',               category: 'core',      muscle_group: 'Abdominales' },
  { id: 'adductors',         category: 'legs',      muscle_group: 'Aductores' },
  { id: 'biceps',            category: 'arms',      muscle_group: 'Bíceps' },
  { id: 'calves',            category: 'legs',      muscle_group: 'Gemelos' },
  { id: 'cardio',            category: 'cardio',    muscle_group: null },
  { id: 'delts',             category: 'shoulders', muscle_group: 'Hombros' },
  { id: 'forearms',          category: 'arms',      muscle_group: 'Antebrazos' },
  { id: 'glutes',            category: 'legs',      muscle_group: 'Glúteos' },
  { id: 'hamstrings',        category: 'legs',      muscle_group: 'Isquiotibiales' },
  { id: 'lats',              category: 'back',      muscle_group: 'Dorsal Ancho' },
  { id: 'levator-scapulae',  category: 'back',      muscle_group: 'Elevador de escápula' },
  { id: 'pectorals',         category: 'chest',     muscle_group: 'Pecho' },
  { id: 'quads',             category: 'legs',      muscle_group: 'Cuádriceps' },
  { id: 'serratus-anterior', category: 'back',      muscle_group: 'Serrato' },
  { id: 'spine',             category: 'core',      muscle_group: 'Espina dorsal' },
  { id: 'traps',             category: 'back',      muscle_group: 'Trapecios' },
  { id: 'triceps',           category: 'arms',      muscle_group: 'Tríceps' },
  { id: 'upper-back',        category: 'back',      muscle_group: 'Espalda Alta' },
];

export default function AdminOptions() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [toast, setToast] = useState(null);
  const [recreating, setRecreating] = useState(false);
  const [confirmRecreate, setConfirmRecreate] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  async function handleRecreate() {
    if (!confirmRecreate) {
      setConfirmRecreate(true);
      return;
    }
    setRecreating(true);
    try {
      const allExercises = [];
      const results = await Promise.allSettled(
        CDN_MUSCLES_ALL.map(muscle =>
          fetch(`${CDN_BASE_URL}/api/es/muscles/${muscle.id}.json`)
            .then(r => r.json())
            .then(data => {
              let exercises;
              if (Array.isArray(data)) {
                exercises = data;
              } else if (Array.isArray(data?.exercises)) {
                exercises = data.exercises;
              } else if (Array.isArray(data?.data)) {
                exercises = data.data;
              } else {
                exercises = Object.values(data || {}).find(v => Array.isArray(v)) || [];
              }
              return { muscle, exercises };
            })
        )
      );

      results.forEach(result => {
        if (result.status === 'fulfilled') {
          const { muscle, exercises } = result.value;
          exercises.forEach(ex => {
            allExercises.push({
              external_id: ex.id,
              name: ex.name,
              category: muscle.category,
              muscle_group: muscle.muscle_group,
              is_cardio: muscle.category === 'cardio',
              is_bodyweight: false,
              gif_url: ex.gifUrl || (ex.file ? `${CDN_BASE_URL}/${ex.file}` : null),
            });
          });
        }
      });

      if (allExercises.length === 0) {
        showToast('No se pudo descargar la biblioteca CDN. Verifica tu conexión.');
        setRecreating(false);
        return;
      }

      const res = await api.post('/admin/recreate-global-exercises', { exercises: allExercises });
      showToast(res.data.message, 'success');
      setConfirmRecreate(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al recrear ejercicios globales');
    } finally {
      setRecreating(false);
    }
  }

  async function handleReset() {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    setResetting(true);
    try {
      const res = await api.post('/admin/reset-all');
      showToast(res.data.message, 'success');
      setConfirmReset(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al resetear los datos');
    } finally {
      setResetting(false);
    }
  }

  async function handleBackup() {
    setDownloading(true);
    try {
      const res = await api.get('/admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gym_tracker_backup_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Backup descargado correctamente', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al crear el backup');
    } finally {
      setDownloading(false);
    }
  }

  async function handleRestore(file) {
    if (!file) return;
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(res.data.message, 'success');
      setConfirmRestore(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al restaurar el backup');
    } finally {
      setRestoring(false);
    }
  }

  function onFileSelected(e) {
    const file = e.target.files[0];
    if (file) {
      setConfirmRestore(true);
    }
  }

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <div className="page">
        <div className="page-header">
          <div className="page-title">Opciones</div>
        </div>

        <div className="admin-tabs px-16">
          <button type="button" className="admin-tab" onClick={() => navigate('/admin')}>Usuarios</button>
          <button type="button" className="admin-tab" onClick={() => navigate('/admin/stats')}>Estadísticas</button>
          <button type="button" className="admin-tab" onClick={() => navigate('/admin/templates')}>Plantillas</button>
          <button type="button" className="admin-tab active" onClick={() => {}}>Opciones</button>
        </div>

        <div className="px-16">

          {/* Backup */}
          <div className="card mb-16" style={{ marginTop: 16 }}>
            <h3 className="mb-8 fw-700" style={{ color: '#eaeaea', fontSize: '1rem' }}>Backup completo</h3>
            <p className="text-muted text-sm mb-12">
              Descarga un archivo JSON con todos los datos de la aplicación (usuarios, ejercicios, rutinas, sesiones, récords).
            </p>
            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={handleBackup}
              disabled={downloading}
            >
              {downloading ? <span className="spinner spinner-sm" /> : '⬇ Descargar backup'}
            </button>
          </div>

          {/* Restore */}
          <div className="card mb-16">
            <h3 className="mb-8 fw-700" style={{ color: '#eaeaea', fontSize: '1rem' }}>Restaurar backup</h3>
            <p className="text-muted text-sm mb-12">
              Sube un archivo JSON de backup generado previamente. <b>Se eliminarán todos los datos actuales</b> y se restaurará el estado del backup.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={onFileSelected}
              style={{ display: 'none' }}
            />

            {confirmRestore ? (
              <div className="admin-confirm-delete" style={{ marginBottom: 12 }}>
                <span className="text-sm">¿Estás seguro? Se <b>sobrescribirán todos los datos actuales</b> con los del backup. Esta acción es irreversible.</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="btn btn-xs btn-secondary" onClick={() => { setConfirmRestore(false); if (fileInputRef.current) fileInputRef.current.value = ''; }}>Cancelar</button>
                  <button type="button" className="btn btn-xs btn-danger" onClick={() => handleRestore(fileInputRef.current?.files[0])} disabled={restoring}>
                    {restoring ? 'Restaurando...' : 'Confirmar y restaurar'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={restoring}
              >
                {restoring ? <span className="spinner spinner-sm" /> : '📁 Seleccionar archivo de backup'}
              </button>
            )}
          </div>

          {/* Reset all */}
          <div className="card mb-16">
            <h3 className="mb-8 fw-700" style={{ color: '#eaeaea', fontSize: '1rem' }}>Resetear datos</h3>
            <p className="text-muted text-sm mb-12">
              Elimina <b>todo</b>: ejercicios, rutinas, sesiones, historial, récords y plantillas. <b>Los usuarios y sus contraseñas no se tocan.</b>
            </p>
            {confirmReset ? (
              <div className="admin-confirm-delete" style={{ marginBottom: 12 }}>
                <span className="text-sm">¿Estás seguro? Esta acción es <b>irreversible</b>. Se borrará todo excepto los usuarios.</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="btn btn-xs btn-secondary" onClick={() => setConfirmReset(false)}>Cancelar</button>
                  <button type="button" className="btn btn-xs btn-danger" onClick={handleReset} disabled={resetting}>
                    {resetting ? 'Reseteando...' : 'Confirmar y resetear'}
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="btn btn-danger btn-full" onClick={handleReset} disabled={resetting}>
                {resetting ? <span className="spinner spinner-sm" /> : '🗑 Resetear todos los datos'}
              </button>
            )}
          </div>

          {/* Recreate exercises */}
          <div className="card mb-16">
            <h3 className="mb-8 fw-700" style={{ color: '#eaeaea', fontSize: '1rem' }}>Ejercicios globales</h3>
            <p className="text-muted text-sm mb-12">
              Descarga los ejercicios de la <b>biblioteca CDN</b> (~1300 ejercicios con GIF animado) y los recrea como ejercicios globales. <b>Se eliminarán todos los ejercicios globales actuales</b> y sus referencias en rutinas, sesiones y récords.
            </p>

            {confirmRecreate ? (
              <div className="admin-confirm-delete" style={{ marginBottom: 12 }}>
                <span className="text-sm">¿Estás seguro? Se eliminarán <b>todos</b> los ejercicios globales y sus datos asociados.</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="btn btn-xs btn-secondary" onClick={() => setConfirmRecreate(false)}>Cancelar</button>
                  <button type="button" className="btn btn-xs btn-danger" onClick={handleRecreate} disabled={recreating}>
                    {recreating ? 'Recreando...' : 'Confirmar y recrear'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-danger btn-full"
                onClick={handleRecreate}
                disabled={recreating}
              >
                {recreating ? <span className="spinner spinner-sm" /> : '🔄 Recrear ejercicios globales'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
